import React, {MutableRefObject} from 'react';

import {Card, List, ListItem, Button, Col, Row, Icon, Text, showConfirm, Menu, MenuItem, Title, NoData, Modal} from '@mc/ui-v4';

import {mcEdit, mcDelete} from '@mc/react-ui-icons';

import {LayerVO, AttributeConfigVO, LayerSettingsApi, LayerSettingsVO, AtributyApi} from '../../../api';
import {QueryClient, useQueryClient} from 'react-query';
import {CACHE_KEY_ATRIBUTY, CACHE_KEY_LAYER_SETTING, CACHE_KEY_LAYER_TOPIC, CACHE_KEY_LAYER_ATTRIBUTES} from '../../../queryKeys';
import {actionApiCall, rffFormSubmit, WaitForQueryData} from '@mc/react-app-v4';
import {MapEditorActions} from '@mc/react-mapio';
import {CACHE_KEY_LAYERS_CONFIG} from '../../../hooks/useLayersConfig';
import {CACHE_KEY_TOPICS_MAP} from '../../../hooks/useTopicsMap';
import {CACHE_KEY_DISCRIMINATOR_LAYERS, useDiscriminatorLayers} from '../../../hooks/useDiscriminatorLayers';
import {UserLayerModal} from './UserLayerModal';
import {ApiCallWaitingModal} from '../ApiCallWaitingModal';
import {useAppConfig} from '@mc/react-app-base';

type OpravneniProps = {
    topicId: string;
    layer: LayerVO;
    atribut: AttributeConfigVO;
    isUserVlastnikOrAdmin?: boolean;
    mapRef: MutableRefObject<MapEditorActions>;
};
export const UserLayerAttributeSymbols: React.FC<OpravneniProps> = ({topicId, isUserVlastnikOrAdmin, layer, atribut, mapRef}) => {
    let appConfig = useAppConfig();
    const queryClient: QueryClient = useQueryClient();

    const diskriminatorLayersQuery = useDiscriminatorLayers(layer.id);

    const sortDiscriminatorLayers = (items: typeof diskriminatorLayersQuery.data) => {
        if (!items?.length) return items;
        return [...items].sort((a, b) => {
            const labelA = a.layer?.label || '';
            const labelB = b.layer?.label || '';
            return labelA.localeCompare(labelB, 'cs');
        });
    };

    const handleZrusitOdliseniPodleAtributu = async (atribut: AttributeConfigVO) => {
        const result = await showConfirm({
            title: 'Vypnout rozlišení podle atributu?',
            message: 'Vypnutím rozlišení podle atributu dojde k odstranení konfigurace symbolů',
            intent: 'danger',
        });
        if (result) {
            await Modal.showWaiting(
                actionApiCall(async () => await AtributyApi.changeDiscriminator(atribut.id, false)),
                ApiCallWaitingModal,
            );
            queryClient.removeQueries([CACHE_KEY_DISCRIMINATOR_LAYERS, layer.id]);
            await queryClient.invalidateQueries([CACHE_KEY_ATRIBUTY, layer.id]);
            await queryClient.invalidateQueries([CACHE_KEY_LAYER_ATTRIBUTES, layer.id]);
        }
    };

    const handleUpdateSublayer = (initialValues: LayerSettingsVO, nazev: string) => {
        const {barva, ...other} = initialValues;

        Modal.show(({onClose}) => (
            <UserLayerModal
                initialValues={{...other, barva: barva ? barva.replace('#', '') : undefined}}
                title={`${atribut.nazev} - ${nazev}`}
                onClose={onClose}
                isEdit
                isSublayer
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        await LayerSettingsApi.updateLayer(data.id, data);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_SETTING, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYERS_CONFIG]);
                        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, topicId]);
                        await queryClient.invalidateQueries([CACHE_KEY_DISCRIMINATOR_LAYERS, layer.id]);
                        mapRef.current?.updateTopic(topicId);
                        onClose();
                    });
                }}
            />
        ));
    };

    return (
        <Card
            mode={'view'}
            title={
                <Title type="card">
                    Odlišit symboly podle hodnot atributu <strong>{atribut.nazev}</strong>
                </Title>
            }
            extra={
                <Button
                    style={'link'}
                    menu={
                        isUserVlastnikOrAdmin && (
                            <Menu>
                                <MenuItem
                                    onClick={() => handleZrusitOdliseniPodleAtributu(atribut)}
                                    text={
                                        <Text intent={'danger'} icon={<Icon icon={mcDelete} />}>
                                            Vypnout odlišení podle hodnot atributu
                                        </Text>
                                    }
                                />
                            </Menu>
                        )
                    }
                />
            }
        >
            <WaitForQueryData queries={[diskriminatorLayersQuery]}>
                {() => (
                    <List compact>
                        {diskriminatorLayersQuery.data?.length > 0 ? (
                            sortDiscriminatorLayers(diskriminatorLayersQuery.data)?.map(item => {
                                const layerImgPath = item.layer?.style?.marker || item.layer?.layerImage;
                                return (
                                    <ListItem bodyClassName="my-0 py-0" key={item.layer.id}>
                                        <Row>
                                            <Col className={'align-items-center d-flex my-auto'} xs={1}>
                                                {layerImgPath && (
                                                    <img
                                                        className={'icon conf-layer-image'}
                                                        src={appConfig.serverContextPath + '/api/files/img/' + layerImgPath + `?t=${Date.now()}`}
                                                        alt={layer.label}
                                                    />
                                                )}
                                            </Col>
                                            <Col>
                                                <Text>{item.layer.label}</Text>
                                            </Col>
                                            <Col className="d-flex justify-content-end">
                                                {isUserVlastnikOrAdmin && (
                                                    <Button
                                                        style={'link'}
                                                        onClick={() => handleUpdateSublayer(item.layerSettings, item.layer.label)}
                                                        icon={<Icon icon={mcEdit} />}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </ListItem>
                                );
                            })
                        ) : (
                            <NoData />
                        )}
                    </List>
                )}
            </WaitForQueryData>
        </Card>
    );
};
